import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form } from "react-bootstrap";
import { useGetAttendanceByMemberIdQuery } from "../../../store/admin/attendanceSlice";
import AttendanceFilter from "./modules/AttendanceFilter";
import { CardComp, TableCompData } from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import { useEffect, useMemo, useState, useCallback } from "react";
import { AttendanceHistoryHeader, MemberAttendanceEntriesHeader } from "../../../utils/TableHeader";
import { status_icon } from "./modules/AttendanceFilter";
import TablePagination from "../../../components/Table/Pagination";
import Profile from "./modules/Profile";
import { PageTitleWithNav } from "../modules/PageHeaders";
import { useViewMemberQuery } from "../../../store/admin/memberManagement/memberSlice";
import { toast } from "react-hot-toast";
import AttendanceStatusChangeModal from "./modules/AttendanceStatusChangeModal";
import { useGetFinesQuery } from "../../../store/admin/settings/fineSlice";
import { resolveAbsenceFine } from "../../../utils/fineUtils";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import moment from "moment";

const MemberAttendanHistory = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const { memberId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialYear = Number(searchParams.get("year")) || new Date().getFullYear();
  const initialMonth = Number(searchParams.get("month")) || new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [allTime, setAllTime] = useState(!(searchParams.get("year") && searchParams.get("month")));
  const navigate = useNavigate();

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const { data: finesDataResponse } = useGetFinesQuery();

  // Extract fines array safely
  const finesList = useMemo(() => {
    if (Array.isArray(finesDataResponse)) return finesDataResponse;
    if (finesDataResponse?.data && Array.isArray(finesDataResponse.data)) return finesDataResponse.data;
    if (finesDataResponse?.fines && Array.isArray(finesDataResponse.fines)) return finesDataResponse.fines;
    return [];
  }, [finesDataResponse]);

  // Resolve absence/attendance fine (category "Attendance" or "Absence", or name fallback; active only)
  const { amount: attendanceFineAmount, statusChangeWindow } = useMemo(
    () => resolveAbsenceFine(finesList),
    [finesList]
  );

  const handleEdit = useCallback((entry) => {
    // Double check lock status before opening modal
    const attendanceDate = moment(entry.attendance_date || entry.created_at);
    if (moment().diff(attendanceDate, 'days') > statusChangeWindow) {
      toast.error(`Security Lock: Attendance status cannot be changed after ${statusChangeWindow} days.`);
      return;
    }
    setSelectedEntry(entry);
    setShowEditModal(true);
  }, [statusChangeWindow]);

  const { data: memberData = {}, isFetching: isFetchingMember } = useViewMemberQuery(memberId);

  const {
    data = {},
    error,
    isFetching,
    isLoading,
  } = useGetAttendanceByMemberIdQuery({
    member_id: memberId,
    page: pageNumber,
    year: allTime ? undefined : selectedYear,
    month: allTime ? undefined : selectedMonth,
  });

  const { attendance_history, attendance_entries, pagination, barProgress, barPercent } =
    useMemo(() => {
      let totalPresent = 0;
      let totalAbsent = 0;
      const { company_attendances = [], attendance_entries = [], pagination = {} } = data;
      const perPage = pagination.perPage || 5;
      const items = company_attendances?.map((attendance, index) => {
        const { attendanceStatus = [], attendanceCount = {} } = attendance;
        totalPresent += attendanceCount.present || 0;
        totalAbsent += attendanceCount.absent || 0;
        const status = attendanceStatus.map((sta) => {
          // Prefer explicit date label like "Tue-04" from API; fallback to created_at
          if (typeof sta?.week_day === "string" && sta?.date) {
            const id = sta?.attendance_status_id ?? sta?.status_id;
            return { ...sta, icon: status_icon[id] };
          }
          const label = sta?.attendance_date || sta?.date;
          if (typeof label === "string" && label.includes("-")) {
            const [day, dt] = label.split("-");
            return {
              ...sta,
              icon: status_icon[sta?.attendance_status_id ?? sta?.status_id],
              week_day: day,
              date: dt,
            };
          }
          const dateObj = sta?.created_at ? new Date(sta.created_at) : null;
          const [day = "", , dt = ""] = dateObj
            ? dateObj.toDateString().split(" ")
            : ["", "", ""];
          return {
            ...sta,
            icon: status_icon[sta?.attendance_status_id ?? sta?.status_id],
            week_day: day,
            date: dt,
          };
        });
        return {
          ...attendance,
          serialNumber: index + 1 + (pageNumber - 1) * perPage,
          attendanceStatus: status,
        };
      });
      // If we have per-entry data, compute totals from it
      if (attendance_entries?.length) {
        totalPresent = attendance_entries.filter((e) => Number(e?.status_id) < 3).length;
        totalAbsent = attendance_entries.filter((e) => Number(e?.status_id) >= 3).length;
      }
      const total = totalAbsent + totalPresent;
      const percent = total > 0 ? (totalPresent / total) * 100 : 0;
      return {
        pagination,
        attendance_history: items,
        attendance_entries: (attendance_entries || []).map((entry, idx) => {
          const attendanceDate = moment(entry.attendance_date || entry.created_at);
          const isLocked = moment().diff(attendanceDate, 'days') > statusChangeWindow;
          
          return {
            ...entry,
            serialNumber: idx + 1 + (pageNumber - 1) * (pagination?.perPage || 10),
            onEdit: handleEdit,
            isLocked,
          };
        }),
        barProgress: percent * 3.6,
        barPercent: percent,
      };
    }, [data, pageNumber, handleEdit, statusChangeWindow]);

  useEffect(() => {
    if (error?.status === 401) {
      toast.error(error?.data?.message || "Login to continue");
      navigate("/login");
    }
  }, [error, navigate]);

  const displayed = allTime ? attendance_entries : attendance_history;
  return (
      <section className="mx-lg-4 min-vh-100 pb-5 mb-5">
        <PageTitleWithNav
          action={() => navigate(-1)}
          highlightText={isFetchingMember ? "Loading..." : `${memberData.first_name || ""} ${memberData.last_name || ""}`}
          title={"Monthly Attendance History"}
        />
        <Profile
          barProgress={barProgress}
          barPercent={barPercent}
          memberData={memberData}
          isLoading={isFetchingMember}
        />
        <CardComp cardClassName={"pt-4"} cardBodyClassName={"py-0"}>
          <div className="d-flex justify-content-between align-items-center px-3 pb-3 flex-wrap gap-3">
            <AttendanceFilter
              showStatusLegend={false}
              onDateChange={(year, month) => {
                if (allTime) return; // ignore calendar when all-time is enabled
                setSelectedYear(year);
                setSelectedMonth(month);
                setPageNumber(1);
              }}
            />
            <Form.Check
              type="switch"
              id="all-time-switch"
              label="All time"
              checked={allTime}
              onChange={(e) => {
                const enabled = e.target.checked;
                setAllTime(enabled);
                setPageNumber(1);
              }}
            />
          </div>
          <div>
            <TableCompData
              marginBottom={"0px"}
              progressPending={isFetching || isLoading}
              progressComponent={<TableSkeletonLoader rows={5} columns={allTime ? 5 : 2} />}
              message={
                !isFetching && !isLoading && displayed.length === 0 ? (
                  <EmptyTable Message={"No record found"} />
                ) : null
              }
              columns={allTime ? MemberAttendanceEntriesHeader() : AttendanceHistoryHeader(true)}
              data={displayed}
            />
          </div>
        </CardComp>
        {!isFetching && !isLoading && displayed.length > 0 && (
          <div className="bg-white py-4 mt-2">
            <TablePagination
              marginTop={"0"}
              handlePageChange={setPageNumber}
              count={displayed.length}
              currentPage={pagination?.currentPage || 1}
              perPage={pagination?.perPage || 5}
              total={pagination?.total || displayed.length}
              totalPages={pagination?.lastPage || 1}
            />
          </div>
        )}

        {/* Edit Attendance Modal */}
        {showEditModal && selectedEntry && (
          <AttendanceStatusChangeModal
            show={showEditModal}
            handleClose={() => setShowEditModal(false)}
            memberData={{
              id: memberId,
              name: `${memberData.first_name} ${memberData.last_name}`,
              member_no: memberData.member_no,
            }}
            currentStatus={{
              id: selectedEntry.status_id,
              name: selectedEntry.status_name,
              entry_id: selectedEntry.id,
            }}
            fineData={selectedEntry.fine || { amount: attendanceFineAmount, status: "PAID" }}
          />
        )}
      </section>
    
  );
};

export default MemberAttendanHistory;

